/**
 * Build stats groups from rows for SmswiserCampaignStatsTab
 * For Media Devoted | Created By Ciro Autiero
 */

import type { GenericStatsRow } from '../generic-stats.types';
import type { GroupByKey } from '../constants';
import { aggregateVoluum } from './voluum';
import { getGenericGroupKey, getGenericSubGroupKey } from '../../table/adapters';
import { sortGroupsByField } from './sort-groups';

export interface StatsGroupGeneric {
  key: string;
  label: string;
  rows: GenericStatsRow[];
  totals: Record<string, number>;
  subGroups?: StatsGroupGeneric[] | null;
}

export function buildGroupsFromRows(
  rows: GenericStatsRow[],
  groupBy: GroupByKey,
  sort: { field: string; dir: 'asc' | 'desc' } | null,
): StatsGroupGeneric[] | null {
  if (groupBy === 'none') return null;

  if (groupBy === 'gateway_leadsList') {
    const byGateway = new Map<string, GenericStatsRow[]>();
    for (const r of rows) {
      const gw = getGenericGroupKey(r, groupBy) || '—';
      const list = byGateway.get(gw) ?? [];
      list.push(r);
      byGateway.set(gw, list);
    }
    const topGroups = Array.from(byGateway.entries()).map(([gwKey, gwRows]) => {
      const byLeads = new Map<string, GenericStatsRow[]>();
      for (const r of gwRows) {
        const ll = getGenericSubGroupKey(r, groupBy) || '—';
        const list = byLeads.get(ll) ?? [];
        list.push(r);
        byLeads.set(ll, list);
      }
      const subGroups = sortGroupsByField(
        Array.from(byLeads.entries()).map(([llKey, llRows]) => ({
          key: `${gwKey}::${llKey}`,
          label: llKey,
          rows: llRows,
          totals: aggregateVoluum(llRows),
        })),
        sort,
      );
      return {
        key: gwKey,
        label: gwKey,
        rows: gwRows,
        totals: aggregateVoluum(gwRows),
        subGroups,
      };
    });
    return sortGroupsByField(topGroups, sort);
  }

  if (groupBy === 'leadsList_gateway') {
    const byLeads = new Map<string, GenericStatsRow[]>();
    for (const r of rows) {
      const ll = getGenericGroupKey(r, groupBy) || '—';
      const list = byLeads.get(ll) ?? [];
      list.push(r);
      byLeads.set(ll, list);
    }
    const topGroups = Array.from(byLeads.entries()).map(([llKey, llRows]) => {
      const byGateway = new Map<string, GenericStatsRow[]>();
      for (const r of llRows) {
        const gw = getGenericSubGroupKey(r, groupBy) || '—';
        const list = byGateway.get(gw) ?? [];
        list.push(r);
        byGateway.set(gw, list);
      }
      const subGroups = sortGroupsByField(
        Array.from(byGateway.entries()).map(([gwKey, gwRows]) => ({
          key: `${llKey}::${gwKey}`,
          label: gwKey,
          rows: gwRows,
          totals: aggregateVoluum(gwRows),
        })),
        sort,
      );
      return {
        key: llKey,
        label: llKey,
        rows: llRows,
        totals: aggregateVoluum(llRows),
        subGroups,
      };
    });
    return sortGroupsByField(topGroups, sort);
  }

  const byKey = new Map<string, GenericStatsRow[]>();
  for (const r of rows) {
    const key = getGenericGroupKey(r, groupBy) || '—';
    const list = byKey.get(key) ?? [];
    list.push(r);
    byKey.set(key, list);
  }
  const flatGroups = Array.from(byKey.entries()).map(([key, groupRows]) => ({
    key,
    label: key,
    rows: groupRows,
    totals: aggregateVoluum(groupRows),
  }));
  return sortGroupsByField(flatGroups, sort);
}
